import type { AdminAiDownRating, AiRatingSurfaceName } from '@steado/admin-contract/ai-ratings';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import { DataTable, type TableColumn, type TableRow } from '@/components/table/DataTable';
import { PaginationFooter } from '@/components/table/PaginationFooter';
import { ScopePills } from '@/components/table/ScopePills';
import { TextCell } from '@/components/table/cells';
import { useAuth } from '@/lib/auth/auth-context';
import { formatDateTime, formatLocalDate, humanise } from '@/lib/format';
import { TabError } from '@/screens/user-detail/TabError';

import { DOWN_RATINGS_PAGE_SIZE, aiDownRatingsQueryOptions } from './ai-ratings-query';

type SurfaceScope = 'all' | AiRatingSurfaceName;

const SCOPES: readonly { readonly id: SurfaceScope; readonly label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'coach_message', label: 'Coach replies' },
  { id: 'weekly_review', label: 'Weekly reviews' },
];

const COLUMNS: readonly TableColumn[] = [
  { key: 'when', label: 'When', width: '1fr' },
  { key: 'who', label: 'User', width: '1.3fr' },
  { key: 'surface', label: 'Rated', width: '1.2fr' },
  { key: 'reason', label: 'Reason', width: '1fr' },
  { key: 'model', label: 'Model', width: '1.4fr' },
];

interface ThumbsDownListProps {
  readonly days: number;
  readonly onOpen: (row: AdminAiDownRating) => void;
}

function detailOf(row: AdminAiDownRating): { label: string; sub: string | null } {
  if (row.surface === 'coach_message') {
    return { label: 'Coach reply', sub: row.sessionId === null ? 'conversation gone' : null };
  }
  return {
    label: row.weekStart === null ? 'Weekly review' : `Review, week of ${formatLocalDate(row.weekStart)}`,
    sub: row.proseSource === null ? null : `prose by ${humanise(row.proseSource)}`,
  };
}

export function ThumbsDownList({ days, onOpen }: ThumbsDownListProps) {
  const { api } = useAuth();
  const [scope, setScope] = useState<SurfaceScope>('all');
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const cursor = cursors[cursors.length - 1];

  const query = useQuery(
    aiDownRatingsQueryOptions(api, { days, surface: scope === 'all' ? undefined : scope, cursor }),
  );

  const onScope = useCallback((next: SurfaceScope) => {
    setScope(next);
    setCursors([undefined]);
  }, []);

  const onNext = useCallback(() => {
    const next = query.data?.pagination.nextCursor;
    if (next === undefined || next === null) return;
    setCursors((stack) => [...stack, next]);
  }, [query.data]);

  const onPrevious = useCallback(() => {
    setCursors((stack) => (stack.length > 1 ? stack.slice(0, -1) : stack));
  }, []);

  const rows: readonly TableRow[] = useMemo(
    () =>
      (query.data?.items ?? []).map((row) => {
        const detail = detailOf(row);
        return {
          id: row.id,
          onOpen: () => {
            onOpen(row);
          },
          cells: [
            <TextCell key="when" value={formatDateTime(row.ratedAt)} mono />,
            <TextCell key="who" value={row.displayName ?? 'Unnamed'} sub={row.phone} weight="medium" />,
            <TextCell key="surface" value={detail.label} sub={detail.sub} />,
            <TextCell
              key="reason"
              value={row.reason === null ? 'none given' : humanise(row.reason)}
              {...(row.reason === null ? { tone: 'muted' as const } : {})}
            />,
            row.model === null ? (
              <TextCell key="model" value="unknown" tone="muted" />
            ) : (
              <TextCell key="model" value={row.model} />
            ),
          ],
        };
      }),
    [query.data, onOpen],
  );

  const pageOffset = (cursors.length - 1) * DOWN_RATINGS_PAGE_SIZE;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-[620px] text-[13px] leading-relaxed text-muted-foreground">
          Thumbs-down from the last {days} days, newest first. A row opens the user where the reply or review
          can be read. The list carries no reply or review text.
        </p>
        <ScopePills label="Thumbs-down surface" scopes={SCOPES} value={scope} onChange={onScope} />
      </div>

      {query.isError ? (
        <TabError message={query.error.message} onRetry={() => void query.refetch()} />
      ) : (
        <DataTable
          caption="Thumbs-down ratings"
          title="Thumbs-down"
          columns={COLUMNS}
          rows={rows}
          isLoading={query.isPending}
          emptyTitle="No thumbs-down in this window"
          emptyBody="Nothing rated down in this window, for this surface."
          footer={
            <PaginationFooter
              rangeStart={rows.length === 0 ? 0 : pageOffset + 1}
              rangeEnd={pageOffset + rows.length}
              hasPrevious={cursors.length > 1}
              hasNext={query.data?.pagination.hasMore ?? false}
              onPrevious={onPrevious}
              onNext={onNext}
              isLoading={query.isPending}
            />
          }
        />
      )}
    </div>
  );
}
